import type { FC } from 'react';
import React, { Fragment, useMemo, useEffect, useContext, memo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { EOP_RECS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery } from '@confluence/query-preloader-tools/entry-points/getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery';
import {
	SetSSRPartialSuccess,
	SSR_PARTIAL_COMPONENT,
} from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess';

import { END_OF_PAGE_RECOMMENDATIONS_METRIC } from './perf.config';
import { EndOfPageRecommendationQuery } from './EndOfPageRecommendationQuery.graphql';
import { transformAndOrderRecs } from './transformers';
import {
	RecommendedPagesSpaceBehavior,
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
} from './__types__/EndOfPageRecommendationQuery';
import { EndOfPageRecPlaceholderContainer } from './SharedComponents';
import { EndOfPageRecommendationComponent } from './EndOfPageRecommendationComponent';

export type EndOfPageRecommendationProps = {
	entityId: string;
	entityType: string;
	pageWidthType: string;
	spaceKey: string;
	isLivePage?: boolean;
};

const EndOfPageRecommendation: FC<EndOfPageRecommendationProps> = memo(
	({ entityType, entityId, pageWidthType, spaceKey, isLivePage = false }) => {
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const [state] = useContentState();
		const skip = entityType !== 'page' && entityType !== 'blogpost';
		const experienceTracker = useContext(ExperienceTrackerContext);

		const { error, loading, data } = useQuery<
			EndOfPageRecommendationData,
			EndOfPageRecommendationQueryVariables
		>(
			...getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery({
				query: EndOfPageRecommendationQuery,
				variables: {
					type: entityType,
					id: entityId,
					exp: expVal(
						'cc_ai_discovery_linked_pages_on_related_pages',
						'experience',
						'end_of_page_v5',
					),
					key: spaceKey,
					keyString: spaceKey,
				},
				context: { single: true },
				errorPolicy: 'all',
				skip,
			}),
		);

		const pageCardData = useMemo(
			() => transformAndOrderRecs(data?.getRecommendedPages?.recommendedPages),
			[data],
		);

		const isEnabled =
			data?.getRecommendedPagesSpaceStatus.recommendedPagesEnabled &&
			data?.getRecommendedPages.status?.isEnabled &&
			data?.getRecommendedPagesSpaceStatus.defaultBehavior === RecommendedPagesSpaceBehavior.SHOWN;

		useEffect(() => {
			if (isEnabled) {
				experienceTracker.start({
					name: EOP_RECS_EXPERIENCE,
				});
			}
		}, [experienceTracker, isEnabled]);

		useEffect(() => {
			if (isEnabled && !loading) {
				if (error) {
					experienceTracker.stopOnError({
						name: EOP_RECS_EXPERIENCE,
						error,
					});
				} else {
					experienceTracker.succeed({
						name: EOP_RECS_EXPERIENCE,
					});
				}
			}
		}, [experienceTracker, isEnabled, loading, error]);

		if (error) {
			return isUnauthorizedError(error) ? null : <ErrorDisplay error={error} />;
		}

		if (skip || !pageCardData || pageCardData.length < 2 || !isEnabled) {
			return null;
		}

		const isContentReady =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR) ||
			window.__SSR_RENDERED__ ||
			state.isContentReady ||
			isLivePage;

		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="end-of-page-rec-wrapper"
				data-testid="end-page-rec-wrapper"
				data-vc="end-of-page-recommendation"
				{...ssrPlaceholderIdProp}
			>
				{isEnabled &&
					isContentReady &&
					(loading ? (
						<UFOLoadHold
							name="EndOfPageRecommendation"
							hold={expValEquals(
								'confluence_end_of_page_recommendation_ufo_hold',
								'isEnabled',
								true,
							)}
						>
							<EndOfPageRecPlaceholderContainer />
						</UFOLoadHold>
					) : (
						<Fragment>
							<PageSegmentLoadStart
								key={entityId}
								metric={END_OF_PAGE_RECOMMENDATIONS_METRIC}
								isCustomStart
							/>
							<EndOfPageRecommendationComponent
								entityId={entityId}
								entityType={entityType}
								pageCardData={pageCardData}
								data={data}
								pageWidthType={pageWidthType}
								spaceKey={spaceKey}
								isLivePage={isLivePage}
							/>
							<SetSSRPartialSuccess name={SSR_PARTIAL_COMPONENT.EndOfPageRecommendation} />
						</Fragment>
					))}
			</div>
		);
	},
);

const EndOfPageRecommendationWithErrorBoundary = (props: EndOfPageRecommendationProps) => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<EndOfPageRecommendation {...props} />
		</ErrorBoundary>
	);
};

export { EndOfPageRecommendationWithErrorBoundary as EndOfPageRecommendation };
